import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { differenceInCalendarDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, Loader2, CheckCircle2 } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { createReservation } from "@/lib/api";

const brl = (n) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

const emptyForm = {
  fullName: "", birthDate: "", cpf: "", email: "", phone: "",
  companionName: "", guests: 1, pet: "Não", paymentMethod: "Pix",
  installments: 1, specialRequests: "",
};

export const Reservation = ({ cabins, selectedId, setSelectedId }) => {
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const cabin = cabins.find((c) => c.id === selectedId) || null;

  const nights = useMemo(() => {
    if (range?.from && range?.to) {
      return Math.max(0, differenceInCalendarDays(range.to, range.from));
    }
    return 0;
  }, [range]);

  const PET_FEE = 200;
  const petFee = form.pet === "Sim" ? PET_FEE : 0;
  const base = cabin && nights > 0 ? cabin.price * nights : 0;
  const total = base > 0 ? base + petFee : 0;

  useEffect(() => {
    if (form.paymentMethod === "Pix") setForm((f) => ({ ...f, installments: 1 }));
  }, [form.paymentMethod]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const canSubmit =
    cabin && nights > 0 && form.fullName && form.cpf && form.email &&
    form.phone && form.birthDate && Number(form.guests) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Preencha as datas, o chalé e os campos obrigatórios.");
      return;
    }
    setLoading(true);
    const payload = {
      cabinId: cabin.id,
      checkIn: format(range.from, "dd/MM/yyyy"),
      checkOut: format(range.to, "dd/MM/yyyy"),
      nights,
      totalPrice: total,
      ...form,
      guests: Number(form.guests),
      installments: Number(form.installments),
    };
    try {
      const res = await createReservation(payload);
      setSuccess({ res });
      setForm(emptyForm);
    } catch (err) {
      toast.error("Não foi possível enviar sua reserva. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservar" className="bg-brand-forest py-24 md:py-36" data-testid="reservation-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="mb-14 max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-bold text-brand-terracotta mb-4">
            Reserve sua estadia
          </p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95] text-brand-cream">
            Escolha as datas e garanta seu refúgio.
          </h2>
          <p className="text-brand-cream/70 mt-5 text-base md:text-lg">
            Selecione primeiro o período da sua estadia, escolha o chalé e preencha seus dados.
            Sem pagamento online — confirmamos tudo com você pelo WhatsApp.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Step 1 — Calendar */}
          <div className="lg:col-span-5 bg-brand-cream p-6 md:p-8" data-testid="reservation-calendar-card">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-brand-terracotta text-sm font-bold tracking-[0.2em]">01</span>
              <h3 className="text-lg font-bold uppercase tracking-[0.15em] text-brand-forest">
                Escolha as datas
              </h3>
            </div>
            <div className="flex justify-center">
              <Calendar
                mode="range"
                numberOfMonths={1}
                selected={range}
                onSelect={setRange}
                disabled={{ before: today }}
                locale={ptBR}
                data-testid="date-range-picker"
              />
            </div>
            <div className="mt-6 border-t border-brand-forest/15 pt-5 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-brand-forest">
                <CalendarDays className="w-4 h-4" />
                {range?.from ? (
                  <span data-testid="selected-dates">
                    {format(range.from, "dd 'de' MMM", { locale: ptBR })}
                    {range?.to ? ` — ${format(range.to, "dd 'de' MMM yyyy", { locale: ptBR })}` : ""}
                  </span>
                ) : (
                  <span className="text-brand-ink/50">Nenhuma data selecionada</span>
                )}
              </div>
              <p className="text-brand-ink/60">
                {nights > 0 ? `${nights} ${nights === 1 ? "diária" : "diárias"}` : "Selecione o check-in e o check-out"}
              </p>
            </div>
          </div>

          {/* Step 2 & 3 — Cabin + Form */}
          <div className="lg:col-span-7 bg-brand-cream p-6 md:p-8" data-testid="reservation-form-card">
            <form onSubmit={handleSubmit} className="space-y-7">
              {/* Step 2 — cabin */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-brand-terracotta text-sm font-bold tracking-[0.2em]">02</span>
                  <h3 className="text-lg font-bold uppercase tracking-[0.15em] text-brand-forest">
                    Escolha o chalé
                  </h3>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {cabins.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      data-testid={`select-cabin-${c.id}`}
                      onClick={() => setSelectedId(c.id)}
                      className={`text-left p-4 border transition-colors duration-300 ${
                        selectedId === c.id
                          ? "border-brand-forest bg-brand-forest text-brand-cream"
                          : "border-brand-forest/20 hover:border-brand-forest/50 text-brand-ink"
                      }`}
                    >
                      <span className="block text-sm font-bold leading-tight">{c.name}</span>
                      <span className={`block text-xs mt-2 ${selectedId === c.id ? "text-brand-cream/80" : "text-brand-ink/60"}`}>
                        {brl(c.price)} / diária
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3 — data */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-brand-terracotta text-sm font-bold tracking-[0.2em]">03</span>
                  <h3 className="text-lg font-bold uppercase tracking-[0.15em] text-brand-forest">
                    Seus dados
                  </h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Nome completo *">
                    <Input data-testid="input-fullName" value={form.fullName}
                      onChange={(e) => update("fullName", e.target.value)} placeholder="Seu nome completo" />
                  </Field>
                  <Field label="Data de nascimento *">
                    <Input data-testid="input-birthDate" type="date" value={form.birthDate}
                      onChange={(e) => update("birthDate", e.target.value)} />
                  </Field>
                  <Field label="CPF *">
                    <Input data-testid="input-cpf" value={form.cpf}
                      onChange={(e) => update("cpf", e.target.value)} placeholder="000.000.000-00" />
                  </Field>
                  <Field label="E-mail *">
                    <Input data-testid="input-email" type="email" value={form.email}
                      onChange={(e) => update("email", e.target.value)} placeholder="voce@email.com" />
                  </Field>
                  <Field label="Telefone (WhatsApp) *">
                    <Input data-testid="input-phone" value={form.phone}
                      onChange={(e) => update("phone", e.target.value)} placeholder="(11) 90000-0000" />
                  </Field>
                  <Field label="Nome do acompanhante">
                    <Input data-testid="input-companionName" value={form.companionName}
                      onChange={(e) => update("companionName", e.target.value)} placeholder="Opcional" />
                  </Field>
                  <Field label="Número de hóspedes *">
                    <Input data-testid="input-guests" type="number" min="1" value={form.guests}
                      onChange={(e) => update("guests", e.target.value)} />
                  </Field>
                  <Field label="Aceita pet? (+ R$ 200,00)">
                    <Select value={form.pet} onValueChange={(v) => update("pet", v)}>
                      <SelectTrigger data-testid="select-pet"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sim">Sim</SelectItem>
                        <SelectItem value="Não">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Forma de pagamento *">
                    <Select value={form.paymentMethod} onValueChange={(v) => update("paymentMethod", v)}>
                      <SelectTrigger data-testid="select-payment"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pix">Pix</SelectItem>
                        <SelectItem value="Cartão de crédito">Cartão de crédito</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {form.paymentMethod === "Cartão de crédito" && (
                    <Field label="Parcelas (sem juros)">
                      <Select value={String(form.installments)} onValueChange={(v) => update("installments", v)}>
                        <SelectTrigger data-testid="select-installments"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}x {total > 0 ? `de ${brl(total / n)}` : "sem juros"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </div>

                <div className="mt-4">
                  <Field label="Alguma solicitação especial?">
                    <Textarea data-testid="input-specialRequests" value={form.specialRequests}
                      onChange={(e) => update("specialRequests", e.target.value)}
                      placeholder="Conte-nos como podemos tornar sua estadia especial..." rows={3} />
                  </Field>
                </div>
              </div>

              {/* Total + submit */}
              <div className="border-t border-brand-forest/20 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div>
                  <span className="block text-xs uppercase tracking-[0.15em] text-brand-ink/50 mb-1">
                    Valor total {nights > 0 ? `· ${nights} ${nights === 1 ? "diária" : "diárias"}` : ""}
                  </span>
                  <span className="text-4xl font-bold text-brand-forest" data-testid="total-price">
                    {brl(total)}
                  </span>
                  {petFee > 0 && total > 0 && (
                    <span className="block text-xs text-brand-ink/60 mt-1" data-testid="pet-fee-note">
                      Inclui taxa de pet: {brl(petFee)}
                    </span>
                  )}
                  {cabin && form.paymentMethod === "Cartão de crédito" && total > 0 && (
                    <span className="block text-xs text-brand-ink/60 mt-1">
                      ou {form.installments}x de {brl(total / form.installments)} sem juros
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || !canSubmit}
                  data-testid="submit-reservation"
                  className="group relative overflow-hidden bg-brand-terracotta text-brand-cream px-9 py-4 text-[13px] uppercase tracking-[0.2em] font-bold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Enviando..." : "Confirmar reserva"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success dialog */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent className="bg-brand-cream border-brand-forest/20" data-testid="success-dialog">
          <div className="flex flex-col items-center text-center py-4">
            <CheckCircle2 className="w-16 h-16 text-brand-forest-light mb-4" />
            <DialogTitle className="text-2xl font-bold text-brand-forest tracking-tight">
              Sua reserva foi concluída com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-brand-ink/70 mt-3">
              Recebemos sua solicitação{success?.res ? ` (código ${success.res.reservationId})` : ""}.
              Em breve entraremos em contato pelo WhatsApp para confirmar sua estadia.
            </DialogDescription>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              data-testid="success-close"
              className="mt-6 inline-flex items-center gap-2 bg-brand-forest text-brand-cream px-8 py-3.5 text-[13px] uppercase tracking-[0.2em] font-bold hover:bg-brand-forest-light transition-colors"
            >
              Fechar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-[0.12em] font-bold text-brand-ink/70">{label}</Label>
    {children}
  </div>
);
